
import React, { FC, Fragment, useState, useMemo, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useAppContext } from "AppContext";
import { parseFilterDate, toFilterDateString } from "utils/filterDates";
import closeXIcon from "images/filters/close-x.svg";
import parisIcon from "images/header/paris-icon.svg";
import brusselsIcon from "images/header/brussels-icon.svg";
import checkCircleGold from "images/header/check-circle-gold.svg";
import circleOutlineTeal from "images/header/circle-outline-teal.svg";
import minusCircleIcon from "images/header/minus-circle.svg";
import plusCircleIcon from "images/header/plus-circle.svg";
import Slider from "rc-slider";
import amenityInternet from "images/filters/amenities/internet.svg";
import amenitySwimmingPool from "images/filters/amenities/swimming-pool.svg";
import amenityParking from "images/filters/amenities/parking.svg";
import amenityWashingMachine from "images/filters/amenities/washing-machine.svg";
import amenityDryingMachine from "images/filters/amenities/drying-machine.svg";
import amenityIron from "images/filters/amenities/iron.svg";
import amenityBabyCot from "images/filters/amenities/baby-cot.svg";
import amenityDishwasher from "images/filters/amenities/dishwasher.svg";
import amenityLinenTowels from "images/filters/amenities/linen-towels.svg";
import amenitySatelliteTv from "images/filters/amenities/satellite-tv.svg";
import amenityTerrace from "images/filters/amenities/terrace.svg";
import amenityHeating from "images/filters/amenities/heating.svg";
import amenityAirConditioning from "images/filters/amenities/air-conditioning.svg";
import amenityCableTv from "images/filters/amenities/cable-tv.svg";
import amenityGarden from "images/filters/amenities/garden.svg";
import { useLocale } from "i18n/locale";

interface MoreFiltersPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const DAY_LETTERS_EN = ["S", "M", "T", "W", "T", "F", "S"] as const;
const DAY_LETTERS_FR = ["D", "L", "M", "M", "J", "V", "S"] as const;

const STAY_TYPES = [
  { id: "travel", labelKey: "filters.stayTypes.travel" },
  { id: "residential_1m", labelKey: "filters.stayTypes.residential1m" },
  { id: "residential_3m", labelKey: "filters.stayTypes.residential3m" },
] as const;

const PROPERTY_RULES = [
  { id: "Pets allowed", labelKey: "filters.rules.petsAllowed" },
  { id: "Family Friendly", labelKey: "filters.rules.familyFriendly" },
  { id: "Smoking allowed", labelKey: "filters.rules.smokingAllowed" },
] as const;

const PROPERTY_TYPES = [
  { id: "Airbnb", labelKey: "filters.propertyTypes.airbnb", descKey: "filters.propertyTypes.airbnbDesc" },
  { id: "Hotel / Apart-hotel", labelKey: "filters.propertyTypes.hotel", descKey: "filters.propertyTypes.hotelDesc" },
  { id: "Residential", labelKey: "filters.propertyTypes.residential", descKey: "filters.propertyTypes.residentialDesc" },
] as const;

const AMENITIES = [
  { id: "Internet", labelKey: "filters.amenities.internet", icon: amenityInternet },
  { id: "Swimming Pool", labelKey: "filters.amenities.swimmingPool", icon: amenitySwimmingPool },
  { id: "Parking", labelKey: "filters.amenities.parking", icon: amenityParking },
  { id: "Washing Machine", labelKey: "filters.amenities.washingMachine", icon: amenityWashingMachine },
  { id: "Drying Machine", labelKey: "filters.amenities.dryingMachine", icon: amenityDryingMachine },
  { id: "Iron & Ironing Board", labelKey: "filters.amenities.ironBoard", icon: amenityIron },
  // id is matched against the stored amenity name ("Baby Cot"); the label is the
  // user-facing text, so the two intentionally differ here.
  { id: "Baby Cot", labelKey: "filters.amenities.babyCot", icon: amenityBabyCot },
  { id: "Dishwasher", labelKey: "filters.amenities.dishwasher", icon: amenityDishwasher },
  { id: "Linen & Towels", labelKey: "filters.amenities.linenTowels", icon: amenityLinenTowels },
  { id: "Satellite TV", labelKey: "filters.amenities.satelliteTV", icon: amenitySatelliteTv },
  { id: "Terrace", labelKey: "filters.amenities.terrace", icon: amenityTerrace },
  { id: "Heating", labelKey: "filters.amenities.heating", icon: amenityHeating },
  { id: "Air Conditioning", labelKey: "filters.amenities.airConditioning", icon: amenityAirConditioning },
  { id: "Cable TV", labelKey: "filters.amenities.cableTV", icon: amenityCableTv },
  { id: "Garden", labelKey: "filters.amenities.garden", icon: amenityGarden },
] as const;

const SAFETY_MEASURES = [
  { id: "Smoke detectors", labelKey: "filters.safety.smokeDetectors" },
  { id: "Carbon monoxide detector", labelKey: "filters.safety.carbonMonoxide" },
  { id: "Fire extinguisher", labelKey: "filters.safety.fireExtinguisher" },
] as const;

const LOCATIONS = [
  { id: "Paris", labelKey: "common.paris", icon: parisIcon },
  { id: "Brussels", labelKey: "common.brussels", icon: brusselsIcon },
] as const;

const GUEST_ROWS = [
  { key: "adults", labelKey: "common.adults", descKey: "common.adultsDesc" },
  { key: "children", labelKey: "common.children", descKey: "common.childrenDesc" },
  { key: "infants", labelKey: "common.infants", descKey: "common.infantsDesc" },
] as const;

const MoreFiltersPanel: FC<MoreFiltersPanelProps> = ({ isOpen, onClose }) => {
  const { t, locale } = useLocale();
  const { filterState, updateFilterState, locationCounts, filterValues, setFilterValues, filteredCount, setAiQuery } = useAppContext();
  const selectedLocation = filterState?.location ?? null;
  const selectedStayTypes = filterValues.stayTypes || [];
  const toggleStayType = (id: string) => {
    const next = selectedStayTypes.includes(id)
      ? selectedStayTypes.filter((s) => s !== id)
      : [...selectedStayTypes, id];
    setFilterValues({ ...filterValues, stayTypes: next });
  };

  const selectedRules = filterValues.propertyRules || [];
  const toggleRule = (id: string) => {
    const next = selectedRules.includes(id)
      ? selectedRules.filter((r) => r !== id)
      : [...selectedRules, id];
    setFilterValues({ ...filterValues, propertyRules: next });
  };

  const selectedPropertyTypes = filterValues.propertyType || [];
  const togglePropertyType = (id: string) => {
    const next = selectedPropertyTypes.includes(id)
      ? selectedPropertyTypes.filter((t) => t !== id)
      : [...selectedPropertyTypes, id];
    setFilterValues({ ...filterValues, propertyType: next });
  };

  const selectedAmenities = filterValues.amenities || [];
  const toggleAmenity = (id: string) => {
    const next = selectedAmenities.includes(id)
      ? selectedAmenities.filter((a) => a !== id)
      : [...selectedAmenities, id];
    setFilterValues({ ...filterValues, amenities: next });
  };

  const selectedSafetyMeasures = filterValues.safetyMeasures || [];
  const toggleSafetyMeasure = (id: string) => {
    const next = selectedSafetyMeasures.includes(id)
      ? selectedSafetyMeasures.filter((s) => s !== id)
      : [...selectedSafetyMeasures, id];
    setFilterValues({ ...filterValues, safetyMeasures: next });
  };

  /* ---- Guest helpers ---- */
  const getGuestValue = (key: string): number => {
    switch (key) {
      case "adults": return filterState?.guestNumber || 0;
      case "children": return filterState?.number_of_children || 0;
      case "infants": return filterState?.number_of_infants || 0;
      default: return 0;
    }
  };

  const setGuestValue = (key: string, value: number) => {
    const v = Math.max(0, value);
    switch (key) {
      case "adults": updateFilterState("guestNumber", v); break;
      case "children": updateFilterState("number_of_children", v); break;
      case "infants": updateFilterState("number_of_infants", v); break;
    }
  };

  const fmtGuestsSummary = () => {
    const adults = filterState?.guestNumber || 0;
    const children = filterState?.number_of_children || 0;
    const infants = filterState?.number_of_infants || 0;
    const people = adults + children + infants;
    if (people === 0) return null;
    return `${people} ${people !== 1 ? t("common.guests") : t("common.guest")}`;
  };

  /* ---- Date picker state ---- */
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [baseMonth, setBaseMonth] = useState(() => {
    const d = parseFilterDate(filterState?.checkInDate);
    if (d) {
      return { year: d.getFullYear(), month: d.getMonth() };
    }
    return { year: today.getFullYear(), month: today.getMonth() };
  });

  // `baseMonth` above is seeded only on first mount. Since this panel stays
  // mounted, a check-in date set afterwards (e.g. by the AI search) wouldn't
  // move the view. So each time the panel opens, jump the calendar to the
  // selected check-in month — otherwise the user has to page forward to reach
  // the dates already applied. Keyed on `isOpen` only, so paging months by hand
  // while the panel is open isn't undone.
  useEffect(() => {
    if (!isOpen) return;
    const d = parseFilterDate(filterState?.checkInDate);
    if (d) setBaseMonth({ year: d.getFullYear(), month: d.getMonth() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startDate = parseFilterDate(filterState?.checkInDate);
  const endDate = parseFilterDate(filterState?.checkOutDate);

  const [selecting, setSelecting] = useState<"start" | "end">(
    startDate && !endDate ? "end" : "start"
  );

  const nextMonth = (y: number, m: number) =>
    m === 11 ? { year: y + 1, month: 0 } : { year: y, month: m + 1 };
  const prevMonth = (y: number, m: number) =>
    m === 0 ? { year: y - 1, month: 11 } : { year: y, month: m - 1 };
  const second = nextMonth(baseMonth.year, baseMonth.month);
  const daysIn = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const firstDow = (y: number, m: number) => new Date(y, m, 1).getDay();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const inRange = (d: Date) => {
    if (!startDate || !endDate) return false;
    return d > startDate && d < endDate;
  };
  const isPast = (d: Date) => d < today;

  const fmtIso = (d: Date) => toFilterDateString(d) ?? "";

  const handleDayClick = (d: Date) => {
    if (isPast(d)) return;
    if (selecting === "start") {
      updateFilterState("checkInDate", fmtIso(d));
      updateFilterState("checkOutDate", "");
      setSelecting("end");
    } else {
      if (d <= startDate!) {
        updateFilterState("checkInDate", fmtIso(d));
        updateFilterState("checkOutDate", "");
        setSelecting("end");
      } else {
        updateFilterState("checkOutDate", fmtIso(d));
        setSelecting("start");
      }
    }
  };

  const buildCells = (y: number, m: number): (Date | null)[] => {
    const cells: (Date | null)[] = [];
    const fd = firstDow(y, m);
    for (let i = 0; i < fd; i++) cells.push(null);
    for (let d = 1; d <= daysIn(y, m); d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };

  const fmtMonthYear = (y: number, m: number) =>
    new Date(y, m, 1).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
      month: "long",
      year: "numeric",
    });

  const fmtRangeLabel = () => {
    if (!startDate || !endDate) return null;
    const ord = (n: number) => {
      if (locale === "fr") return `${n}`;
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    const m = (d: Date) =>
      d.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", { month: "short" });
    return `${ord(startDate.getDate())} ${m(startDate)} - ${ord(endDate.getDate())} ${m(endDate)}`;
  };

  const renderCompactMonth = (y: number, m: number) => {
    const cells = buildCells(y, m);
    const rows: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

    return (
      <div className="flex-1">
        {/* Month name */}
        <p
          className="text-[12px] leading-[24px] text-[#01152E] text-center m-0"
          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
        >
          {fmtMonthYear(y, m)}
        </p>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 mt-[8px]">
          {(locale === "fr" ? DAY_LETTERS_FR : DAY_LETTERS_EN).map((letter, i) => (
            <div key={i} className="flex justify-center p-[4px]">
              <span
                className="text-[8px] uppercase text-[#445162]"
                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
              >
                {letter}
              </span>
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="flex flex-col gap-[4px] mt-[4px]">
          {rows.map((row, ri) => (
            <div key={ri} className="grid grid-cols-7">
              {row.map((date, ci) => {
                if (!date) return <div key={ci} className="h-[24px]" />;

                const isStart = !!(startDate && sameDay(date, startDate));
                const isEnd = !!(endDate && sameDay(date, endDate));
                const ranged = inRange(date);
                const past = isPast(date);

                let rangeBg: "full" | "right" | "left" | "none" = "none";
                if (ranged) rangeBg = "full";
                else if (isStart && endDate) rangeBg = "right";
                else if (isEnd && startDate) rangeBg = "left";

                return (
                  <div
                    key={ci}
                    className="relative flex items-center justify-center h-[24px]"
                  >
                    {rangeBg === "full" && (
                      <div className="absolute inset-0 bg-[#faf7f1]" />
                    )}
                    {rangeBg === "right" && (
                      <div className="absolute inset-y-0 left-1/2 right-0 bg-[#faf7f1]" />
                    )}
                    {rangeBg === "left" && (
                      <div className="absolute inset-y-0 left-0 right-1/2 bg-[#faf7f1]" />
                    )}
                    <button
                      onClick={() => handleDayClick(date)}
                      disabled={past}
                      className={`relative flex items-center justify-center w-[24px] h-[24px] shrink-0 border-0 p-0 ${
                        past ? "cursor-default" : "cursor-pointer"
                      }`}
                      style={{
                        background:
                          isStart || isEnd ? "#B88333" : "transparent",
                        borderRadius: isStart || isEnd ? "50%" : undefined,
                      }}
                    >
                      <span
                        className="text-[10px] text-center"
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontWeight: 500,
                          color:
                            isStart || isEnd
                              ? "#ffffff"
                              : past
                              ? "#d6d6d6"
                              : "#01152E",
                        }}
                      >
                        {date.getDate()}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        {/* Panel container — anchored to the right */}
        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel
              className="w-[448px] max-w-full h-full bg-white flex flex-col"
              style={{ boxShadow: "-4px 0 24px rgba(0,0,0,0.12)" }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-[20px] py-[20px]">
                <h2
                  className="text-[16px] leading-[24px] text-[#01152E] m-0"
                  style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                >
                  {t("common.filters")}
                </h2>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-[#F5F5F5] transition-colors cursor-pointer bg-transparent border-0 p-0"
                >
                  <img src={closeXIcon} alt={t("filters.close")} width={12} height={12} />
                </button>
              </div>

              {/* Separator */}
              <div className="border-t border-[#D6D6D6]" />

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto px-[24px] pt-[24px]">
                {/* Location section */}
                <div className="flex flex-col gap-[21px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("common.location")}
                  </p>
                  <div className="flex gap-[22px]">
                    {LOCATIONS.map((loc) => {
                      const isSelected = selectedLocation === loc.id;
                      return (
                        <button
                          key={loc.id}
                          onClick={() =>
                            updateFilterState(
                              "location",
                              isSelected ? null : loc.id
                            )
                          }
                          className={`flex items-center flex-1 min-w-0 px-[14px] py-[16px] rounded-[12px] border bg-white cursor-pointer gap-[4px] ${
                            isSelected
                              ? "border-[#445162]"
                              : "border-[#d6d6d6]"
                          }`}
                        >
                          <img
                            src={loc.icon}
                            alt=""
                            width={40}
                            height={40}
                            className="flex-shrink-0"
                          />
                          <div className="flex flex-col flex-1 min-w-0">
                            <p
                              className="text-[16px] leading-none text-[#01152E] text-left m-0 truncate"
                              style={{
                                fontFamily: "Poppins, sans-serif",
                                fontWeight: 500,
                              }}
                            >
                              {t(loc.labelKey)}
                            </p>
                            <p
                              className="text-[8px] leading-none uppercase tracking-[0.8px] text-[#445162] text-left m-0 mt-[6px] truncate"
                              style={{
                                fontFamily: "Poppins, sans-serif",
                                fontWeight: 400,
                              }}
                            >
                              {locationCounts[loc.id] != null
                                ? `${locationCounts[loc.id]} ${t("common.properties")}`
                                : "\u00A0"}
                            </p>
                          </div>
                          <img
                            src={
                              isSelected ? checkCircleGold : circleOutlineTeal
                            }
                            alt=""
                            width={20}
                            height={20}
                            className="flex-shrink-0"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Dates section */}
                <div className="flex flex-col gap-[21px]">
                  {/* Section header */}
                  <div className="flex items-center gap-[21px]">
                    <p
                      className="text-[16px] leading-[24px] text-[#01152E] m-0"
                      style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                    >
                      {t("common.dates")}
                    </p>
                    {fmtRangeLabel() && (
                      <p
                        className="flex-1 text-[10px] leading-[24px] text-[#B88333] text-right m-0"
                        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                      >
                        {fmtRangeLabel()}
                      </p>
                    )}
                  </div>

                  {/* Month nav + compact two-month calendar */}
                  <div>
                    <div className="flex items-center mb-[8px]">
                      <button
                        onClick={() =>
                          setBaseMonth(prevMonth(baseMonth.year, baseMonth.month))
                        }
                        className="cursor-pointer bg-transparent border-0 p-[4px] flex items-center"
                      >
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                          <path d="M7 1L1 7L7 13" stroke="#01152E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                      <div className="flex-1" />
                      <button
                        onClick={() =>
                          setBaseMonth(nextMonth(baseMonth.year, baseMonth.month))
                        }
                        className="cursor-pointer bg-transparent border-0 p-[4px] flex items-center"
                      >
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                          <path d="M1 1L7 7L1 13" stroke="#01152E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex gap-[16px]">
                      {renderCompactMonth(baseMonth.year, baseMonth.month)}
                      {renderCompactMonth(second.year, second.month)}
                    </div>
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Guests section */}
                <div className="flex flex-col gap-[21px]">
                  {/* Section header */}
                  <div className="flex items-center gap-[21px]">
                    <p
                      className="text-[16px] leading-[24px] text-[#01152E] m-0"
                      style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                    >
                      {t("common.guests")}
                    </p>
                    {fmtGuestsSummary() && (
                      <p
                        className="flex-1 text-[10px] leading-[24px] text-[#B88333] text-right m-0"
                        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                      >
                        {fmtGuestsSummary()}
                      </p>
                    )}
                  </div>

                  {/* Guest rows */}
                  {GUEST_ROWS.map((row) => {
                    const value = getGuestValue(row.key);
                    return (
                      <div key={row.key} className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <p
                            className="text-[14px] leading-none text-[#01152E] m-0"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                          >
                            {t(row.labelKey)}
                          </p>
                          <p
                            className="text-[10px] leading-[12px] text-[#445162] m-0 mt-[4px]"
                            style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
                          >
                            {t(row.descKey)}
                          </p>
                        </div>
                        <div className="flex items-center gap-[17px]">
                          <button
                            onClick={() => setGuestValue(row.key, value - 1)}
                            className="cursor-pointer bg-transparent border-0 p-0 flex"
                          >
                            <img src={minusCircleIcon} alt="−" width={24} height={24} />
                          </button>
                          <span
                            className="text-[16px] leading-[24px] text-[#01152E] w-[12px] text-center"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                          >
                            {value}
                          </span>
                          <button
                            onClick={() => setGuestValue(row.key, value + 1)}
                            className="cursor-pointer bg-transparent border-0 p-0 flex"
                          >
                            <img src={plusCircleIcon} alt="+" width={24} height={24} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Price Range section */}
                <div className="flex flex-col gap-[21px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("filters.priceRange")}
                  </p>
                  <div>
                    <Slider
                      range
                      min={0}
                      max={2000}
                      value={filterValues.rangePrices as unknown as number[]}
                      allowCross={false}
                      onChange={(e) =>
                        setFilterValues({ ...filterValues, rangePrices: e as unknown as string[] })
                      }
                      trackStyle={[{ backgroundColor: "#B88333", height: 4 }]}
                      railStyle={{ backgroundColor: "#d6d6d6", height: 4 }}
                      handleStyle={[
                        {
                          backgroundColor: "#ffffff",
                          borderColor: "#01152E",
                          borderWidth: 2,
                          width: 16,
                          height: 16,
                          marginTop: -6,
                          opacity: 1,
                          boxShadow: "none",
                        },
                        {
                          backgroundColor: "#ffffff",
                          borderColor: "#01152E",
                          borderWidth: 2,
                          width: 16,
                          height: 16,
                          marginTop: -6,
                          opacity: 1,
                          boxShadow: "none",
                        },
                      ]}
                    />
                    <div className="flex justify-between mt-[12px]">
                      <span
                        className="text-[12px] leading-[12px] text-[#445162]"
                        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                      >
                        €{(filterValues.rangePrices as unknown as number[])[0]}
                      </span>
                      <span
                        className="text-[12px] leading-[12px] text-[#445162]"
                        style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                      >
                        €{(filterValues.rangePrices as unknown as number[])[1]}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Rooms and beds section */}
                <div className="flex flex-col gap-[21px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("filters.roomsAndBeds")}
                  </p>
                  <div className="flex flex-col gap-[14px]">
                    {([
                      { key: "bedrooms" as const, labelKey: "payDone.bedrooms" },
                      { key: "bathrooms" as const, labelKey: "payDone.bathrooms" },
                      { key: "toilets" as const, labelKey: "payDone.toilets" },
                    ]).map((row) => {
                      const value = filterValues[row.key] || 0;
                      return (
                        <div key={row.key} className="flex items-center justify-between">
                          <p
                            className="text-[14px] leading-[24px] text-[#445162] m-0"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                          >
                            {t(row.labelKey)}
                          </p>
                          <div className="flex items-center gap-[14px]">
                            <button
                              onClick={() =>
                                setFilterValues({ ...filterValues, [row.key]: Math.max(0, value - 1) })
                              }
                              className="cursor-pointer bg-transparent border-0 p-0 flex"
                            >
                              <img src={minusCircleIcon} alt="−" width={24} height={24} />
                            </button>
                            <span
                              className="text-[16px] leading-[24px] text-[#01152E] w-[12px] text-center"
                              style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                            >
                              {value}
                            </span>
                            <button
                              onClick={() =>
                                setFilterValues({ ...filterValues, [row.key]: value + 1 })
                              }
                              className="cursor-pointer bg-transparent border-0 p-0 flex"
                            >
                              <img src={plusCircleIcon} alt="+" width={24} height={24} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Stays section */}
                <div className="flex flex-col gap-[21px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("filters.stays")}
                  </p>
                  <div className="flex flex-wrap gap-x-[40px] gap-y-[14px]">
                    {STAY_TYPES.map((st) => {
                      const checked = selectedStayTypes.includes(st.id);
                      return (
                        <button
                          key={st.id}
                          onClick={() => toggleStayType(st.id)}
                          className="flex items-center gap-[10px] bg-transparent border-0 p-0 cursor-pointer shrink-0"
                        >
                          {checked ? (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="14" height="14" rx="3" fill="#B88333" />
                              <path d="M3.5 7L6 9.5L10.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <div className="w-[14px] h-[14px] rounded-[3px] border border-[#d6d6d6] box-border" />
                          )}
                          <span
                            className="text-[14px] leading-[24px] text-[#445162] whitespace-nowrap"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                          >
                            {t(st.labelKey)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Property Rules section */}
                <div className="flex flex-col gap-[21px] pb-[24px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("filters.propertyRules")}
                  </p>
                  <div className="flex flex-wrap gap-x-[40px] gap-y-[14px]">
                    {PROPERTY_RULES.map((rule) => {
                      const checked = selectedRules.includes(rule.id);
                      return (
                        <button
                          key={rule.id}
                          onClick={() => toggleRule(rule.id)}
                          className="flex items-center gap-[10px] bg-transparent border-0 p-0 cursor-pointer shrink-0"
                        >
                          {checked ? (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="14" height="14" rx="3" fill="#B88333" />
                              <path d="M3.5 7L6 9.5L10.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <div className="w-[14px] h-[14px] rounded-[3px] border border-[#d6d6d6] box-border" />
                          )}
                          <span
                            className="text-[14px] leading-[24px] text-[#445162] whitespace-nowrap"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                          >
                            {t(rule.labelKey)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Property Type section */}
                <div className="flex flex-col gap-[21px] pb-[24px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("filters.propertyType")}
                  </p>
                  <div className="flex flex-col gap-[20px]">
                    {PROPERTY_TYPES.map((pt) => {
                      const checked = selectedPropertyTypes.includes(pt.id);
                      return (
                        <button
                          key={pt.id}
                          onClick={() => togglePropertyType(pt.id)}
                          className="flex items-start gap-[10px] bg-transparent border-0 p-0 cursor-pointer text-left w-full"
                        >
                          <div className="mt-[5px] shrink-0">
                            {checked ? (
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="14" height="14" rx="3" fill="#B88333" />
                                <path d="M3.5 7L6 9.5L10.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <div className="w-[14px] h-[14px] rounded-[3px] border border-[#d6d6d6] box-border" />
                            )}
                          </div>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span
                              className="text-[14px] leading-[24px] text-[#01152E]"
                              style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                            >
                              {t(pt.labelKey)}
                            </span>
                            {"descKey" in pt && pt.descKey && (
                              <span
                                className="text-[10px] leading-[12px] text-[#445162]"
                                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400 }}
                              >
                                {t(pt.descKey)}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Amenities section */}
                <div className="flex flex-col gap-[21px] pb-[24px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("listingStayDetail.amenities")}
                  </p>
                  <div className="flex flex-wrap gap-[10px_6px]">
                    {AMENITIES.map((am) => {
                      const selected = selectedAmenities.includes(am.id);
                      return (
                        <button
                          key={am.id}
                          onClick={() => toggleAmenity(am.id)}
                          className="flex items-center gap-[10px] h-[30px] px-[12px] rounded-[20px] border border-solid cursor-pointer shrink-0"
                          style={{
                            backgroundColor: selected ? "#B88333" : "transparent",
                            borderColor: selected ? "#B88333" : "#D6D6D6",
                          }}
                        >
                          <img
                            src={am.icon}
                            alt=""
                            className="h-[14px] w-auto"
                            style={{ filter: selected ? "brightness(0) invert(1)" : "none" }}
                          />
                          <span
                            className="text-[12px] leading-[24px] whitespace-nowrap"
                            style={{
                              fontFamily: "Poppins, sans-serif",
                              fontWeight: 500,
                              color: selected ? "#FFFFFF" : "#01152E",
                            }}
                          >
                            {t(am.labelKey)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Separator */}
                <div className="my-[24px] -mx-[24px] border-t border-[#D6D6D6]" />

                {/* Safety Measures section */}
                <div className="flex flex-col gap-[21px] pb-[24px]">
                  <p
                    className="text-[16px] leading-[24px] text-[#01152E] m-0"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}
                  >
                    {t("filters.safetyMeasures")}
                  </p>
                  <div className="flex flex-wrap gap-x-[40px] gap-y-[14px]">
                    {SAFETY_MEASURES.map((sm) => {
                      const checked = selectedSafetyMeasures.includes(sm.id);
                      return (
                        <button
                          key={sm.id}
                          onClick={() => toggleSafetyMeasure(sm.id)}
                          className="flex items-center gap-[10px] bg-transparent border-0 p-0 cursor-pointer shrink-0"
                        >
                          {checked ? (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="14" height="14" rx="3" fill="#B88333" />
                              <path d="M3.5 7L6 9.5L10.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <div className="w-[14px] h-[14px] rounded-[3px] border border-[#d6d6d6] box-border" />
                          )}
                          <span
                            className="text-[14px] leading-[24px] text-[#01152E] whitespace-nowrap"
                            style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                          >
                            {t(sm.labelKey)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="flex items-center justify-between px-[24px] py-[15px] bg-white"
                style={{ boxShadow: "0px 0px 10px rgba(0,0,0,0.15)" }}
              >
                <button
                  onClick={() => {
                    setFilterValues({
                      toilets: 0,
                      bedrooms: 0,
                      bathrooms: 0,
                      propertyType: [],
                      rangePrices: [0, 1000] as unknown as string[],
                      propertyRules: [],
                      amenities: [],
                      safetyMeasures: [],
                      stayTypes: [],
                    });
                    updateFilterState("checkInDate", null);
                    updateFilterState("checkOutDate", null);
                    updateFilterState("guestNumber", 1);
                    updateFilterState("number_of_children", 0);
                    updateFilterState("number_of_infants", 0);
                    updateFilterState("number_of_pets", 0);
                    updateFilterState("location", null);
                    // Also drop any "near <place>" target so the proximity sort
                    // is released along with the rest of the filters.
                    updateFilterState("nearbyArea", null);
                    updateFilterState("nearbyPlace", null);
                    // Dates were just cleared, so send the calendar back to
                    // today's month and reset the range-picking state — otherwise
                    // it stays parked on whatever month the old selection showed.
                    setBaseMonth({ year: today.getFullYear(), month: today.getMonth() });
                    setSelecting("start");
                    // The AI search box produced these filters, so clearing the
                    // filters should clear its text too — otherwise the box shows
                    // a stale query that no longer matches the applied filters.
                    setAiQuery("");
                  }}
                  className="flex items-center justify-center h-[30px] px-[12px] py-[10px] rounded-[20px] border border-solid border-[#d6d6d6] bg-white cursor-pointer"
                >
                  <span
                    className="text-[12px] leading-[24px] text-[#01152E] whitespace-nowrap"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                  >
                    {t("filters.clearAll")}
                  </span>
                </button>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center h-[30px] px-[24px] py-[10px] rounded-[30px] bg-[#01152E] border-0 cursor-pointer"
                >
                  <span
                    className="text-[14px] leading-[24px] text-white whitespace-nowrap"
                    style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
                  >
                    {t("filters.show")} {filteredCount} {filteredCount !== 1 ? t("filters.residences") : t("filters.residence")}
                  </span>
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MoreFiltersPanel;
